/**
 * PRASHASTA-CONCIERGE-01: Smart Conversational Enquiry Widget
 *
 * Floating WhatsApp-style chat launcher that guides visitors through
 * a tenant-configurable event enquiry flow and submits to CRM.
 *
 * Features:
 * - Floating launcher (bottom-right, mobile-first)
 * - Optional welcome teaser with dismissal
 * - Expandable chat panel with branded header
 * - Sequential questions driven by server-side tenant flow config
 * - Quick-reply chips + free-text / date / phone / email / multi-select
 * - Progress bar
 * - Back navigation
 * - Session persistence in localStorage
 * - WhatsApp handoff Mode A (click-to-chat) + Mode B (mock)
 * - Loading and error states
 * - Restart conversation
 * - Keyboard accessible
 * - Responsive — tested at 390px and 1920px
 */

import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { X, MessageCircle, ChevronLeft, Send, Phone, Mail, RotateCcw } from "lucide-react";
import EnquiryConfirmationScreen from "./EnquiryConfirmationScreen";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Session storage keys ──────────────────────────────────────────────────────
const SESS_KEY = "prashasta_concierge_session";
const VID_KEY = "prashasta_vid";
const SID_KEY = "prashasta_sid";

// ── Alias-scoped popup storage helpers ────────────────────────────────────────
// Keys are scoped by tenant alias so two tenant widgets in the same browser
// never share popup-seen state.
// All reads/writes are wrapped in try/catch — storage failures (private browsing,
// quota exceeded, security policies) must never break widget rendering.
function _popupSeenKey(alias) { return `pra_popup_seen_${alias}`; }
function _popupSessKey(alias) { return `pra_popup_sess_${alias}`; }
function _safeLocalGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function _safeLocalSet(key, val) { try { localStorage.setItem(key, val); } catch { /* silenced */ } }
function _safeSessGet(key) { try { return sessionStorage.getItem(key); } catch { return null; } }
function _safeSessSet(key, val) { try { sessionStorage.setItem(key, val); } catch { /* silenced */ } }

// Chat-open state persistence key (session-scoped, survives SPA navigation re-mounts)
function _chatOpenKey(alias) { return `pra_chat_open_${alias}`; }

function getVisitorId() {
  try {
    let vid = localStorage.getItem(VID_KEY);
    if (!vid) { vid = crypto.randomUUID ? crypto.randomUUID() : `v-${Date.now()}`; localStorage.setItem(VID_KEY, vid); }
    return vid;
  } catch { return `v-${Date.now()}`; }
}
function getAnalyticsSessionId() {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) { sid = `s-${Date.now()}`; sessionStorage.setItem(SID_KEY, sid); }
    return sid;
  } catch { return `s-${Date.now()}`; }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadSession() {
  try { return JSON.parse(localStorage.getItem(SESS_KEY) || "null"); } catch { return null; }
}
function saveSession(data) {
  try { localStorage.setItem(SESS_KEY, JSON.stringify(data)); } catch {}
}
function clearSession() {
  try { localStorage.removeItem(SESS_KEY); } catch {}
}

// Build a WhatsApp click-to-chat URL (Mode A)
function buildWhatsAppUrl(number, message) {
  const clean = (number || "").replace(/\D/g, "");
  const encoded = encodeURIComponent(message || "Hello Prashasta!");
  return clean ? `https://wa.me/${clean}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
}

// ── Input sub-components ──────────────────────────────────────────────────────

function QuickReplies({ options, selected, onSelect, multi }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
      {options.map(opt => {
        const isSelected = multi
          ? (Array.isArray(selected) && selected.includes(opt.value))
          : selected === opt.value;
        return (
          <button
            key={opt.id}
            data-testid={`concierge-option-${opt.value}`}
            onClick={() => onSelect(opt.value)}
            style={{
              padding: "8px 16px",
              borderRadius: "20px",
              border: isSelected ? "2px solid #C9A15F" : "1.5px solid #d1c4a8",
              background: isSelected ? "#fdf6ec" : "#fff",
              color: isSelected ? "#9a7133" : "#3c3028",
              fontSize: "14px",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: isSelected ? "600" : "400",
              transition: "all 0.15s",
              outline: "none",
              boxShadow: isSelected ? "0 2px 8px #C9A15F30" : "none",
            }}
            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = "#C9A15F"; }}
            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = "#d1c4a8"; }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ placeholder, value, onChange, onSubmit, type = "text" }) {
  return (
    <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || "Type your answer…"}
        data-testid="concierge-text-input"
        onKeyDown={e => { if (e.key === "Enter" && value.trim()) onSubmit(); }}
        style={{
          flex: 1, padding: "10px 14px", borderRadius: "12px",
          border: "1.5px solid #d1c4a8", fontSize: "14px",
          fontFamily: "'DM Sans', sans-serif", outline: "none",
          background: "#fafaf8", color: "#3c3028",
        }}
        autoFocus
      />
      <button
        onClick={onSubmit}
        disabled={!value.trim()}
        data-testid="concierge-text-submit"
        style={{
          padding: "10px 16px", borderRadius: "12px",
          background: value.trim() ? "#C9A15F" : "#e0d5c0",
          border: "none", color: "#fff", cursor: value.trim() ? "pointer" : "not-allowed",
          transition: "background 0.15s",
        }}
      >
        <Send size={16} />
      </button>
    </div>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────

export default function ConciergeWidget({ alias }) {
  // Restore open state from sessionStorage so SPA navigation never resets the panel
  const [open, setOpenState] = useState(() => _safeSessGet(_chatOpenKey(alias)) === "1");
  const setOpen = useCallback((val) => {
    setOpenState(val);
    if (val) _safeSessSet(_chatOpenKey(alias), "1");
    else _safeSessSet(_chatOpenKey(alias), "0");
  }, [alias]);
  const [teaserVisible, setTeaserVisible] = useState(false);
  const [settings, setSettings] = useState(null);
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [stepHistory, setStepHistory] = useState([]); // [{question_id, answer}]
  const [collectedData, setCollectedData] = useState({});
  const [textInput, setTextInput] = useState("");
  const [multiSelect, setMultiSelect] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [publicReference, setPublicReference] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const chatEndRef = useRef(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (open && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, stepHistory, currentQuestionId]);

  // Load settings on mount
  useEffect(() => {
    if (!alias) return;
    axios.get(`${BASE}/public/concierge/${alias}/settings`).then(r => {
      setSettings(r.data);
      // Alias-scoped keys ensure two tenant widgets in the same browser never
      // share popup-seen state.  All storage calls are wrapped so a SecurityError
      // (private browsing, quota) can never crash the widget.
      const isReturningVisitor = _safeLocalGet(_popupSeenKey(alias));
      const handledThisVisit  = _safeSessGet(_popupSessKey(alias));
      const autoPopupEnabled  = r.data?.show_teaser !== false;
      const delayMs = ((r.data?.auto_popup_delay_seconds ?? 5)) * 1000;
      // Respect prefers-reduced-motion — keep launcher visible but skip animated auto-pop
      const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (autoPopupEnabled && !isReturningVisitor && !handledThisVisit && !prefersReduced) {
        setTimeout(() => {
          setTeaserVisible(true);
          // Mark: returning-visitor flag (localStorage, cross-session)
          _safeLocalSet(_popupSeenKey(alias), "1");
          // Mark: handled this visit (sessionStorage, cleared on tab close)
          _safeSessSet(_popupSessKey(alias), "shown");
        }, delayMs);
      }
    }).catch(() => setSettings({ enabled: true, show_teaser: true, teaser_text: "Need help planning your event?", auto_popup_delay_seconds: 5 }));
  }, [alias]);

  // Restore session from localStorage
  useEffect(() => {
    const saved = loadSession();
    if (saved && saved.alias === alias && !saved.submitted) {
      setSessionId(saved.sessionId);
      setCurrentQuestionId(saved.currentQuestionId);
      setStepHistory(saved.stepHistory || []);
      setCollectedData(saved.collected || {});
      setFlow(saved.flow || null);
    }
  }, [alias]);

  // Persist session
  useEffect(() => {
    if (sessionId) {
      saveSession({ alias, sessionId, currentQuestionId, stepHistory, collected: collectedData, flow, submitted });
    }
  }, [sessionId, currentQuestionId, stepHistory, collectedData, flow, submitted, alias]);

  const loadFlow = useCallback(async () => {
    if (flow) return flow;
    try {
      const r = await axios.get(`${BASE}/public/concierge/${alias}/flow`);
      setFlow(r.data);
      return r.data;
    } catch (e) {
      throw new Error("Could not load conversation flow. Please try again.");
    }
  }, [alias, flow]);

  const startSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const f = await loadFlow();
      setFlow(f); // Always ensure flow is in React state (handles restart path where loadFlow returns cached value without calling setFlow)
      const r = await axios.post(`${BASE}/public/concierge/${alias}/session`, {
        visitor_id: getVisitorId(),
        analytics_session_id: getAnalyticsSessionId(),
        source_page: window.location.pathname,
      });
      setSessionId(r.data.session_id);
      setCurrentQuestionId(r.data.start_question_id || f.start_question_id);
      setStepHistory([]);
      setCollectedData({});
      setSubmitted(false);
      setPublicReference(null);
    } catch (e) {
      setError(e.message || "Failed to start conversation. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [alias, loadFlow]);

  const handleOpen = useCallback(() => {
    setTeaserVisible(false);
    _safeSessSet(_popupSessKey(alias), "opened");
    setOpen(true);
    if (!sessionId) startSession();
  }, [alias, sessionId, startSession]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Record abandonment if session active and not submitted
    if (sessionId && !submitted) {
      axios.post(`${BASE}/public/concierge/session/${sessionId}/abandon`).catch(() => {});
    }
  }, [sessionId, submitted]);

  const handleRestart = useCallback(() => {
    clearSession();
    setSessionId(null);
    setCurrentQuestionId(null);
    setStepHistory([]);
    setCollectedData({});
    setSubmitted(false);
    setPublicReference(null);
    // Do NOT reset flow to null — it is tenant-specific and unchanged between sessions.
    // Resetting to null caused a blank screen because loadFlow() returned the stale
    // closure value without calling setFlow(), leaving flow=null after re-render.
    setTextInput("");
    setMultiSelect([]);
    setError(null);
    startSession();
  }, [startSession]);

  // Current question object
  const currentQuestion = flow && currentQuestionId
    ? (flow.questions || []).find(q => q.id === currentQuestionId)
    : null;

  // Progress calculation
  const totalQs = flow?.questions?.length || 1;
  const answeredQs = stepHistory.length;
  const progressPct = Math.min(100, Math.round((answeredQs / totalQs) * 100));

  const submitStep = useCallback(async (questionId, answer) => {
    if (!sessionId || !questionId) return;
    const q = (flow?.questions || []).find(q => q.id === questionId);
    if (!q) return;
    try {
      const r = await axios.post(`${BASE}/public/concierge/session/${sessionId}/step`, {
        question_id: questionId,
        answer,
        answered_at: new Date().toISOString(),
      });
      // Update local state
      setStepHistory(prev => [...prev, { question_id: questionId, question_text: q.text, answer }]);
      setCollectedData(prev => ({ ...prev, [q.field_key]: answer }));
      setTextInput("");
      setMultiSelect([]);
      if (r.data.is_last || !r.data.next_question_id) {
        setCurrentQuestionId(null);
      } else {
        setCurrentQuestionId(r.data.next_question_id);
      }
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
  }, [sessionId, flow]);

  const handleBack = useCallback(async () => {
    if (!sessionId || stepHistory.length === 0) return;
    try {
      const r = await axios.post(`${BASE}/public/concierge/session/${sessionId}/back`);
      const prevHistory = stepHistory.slice(0, -1);
      const prevStep = stepHistory[stepHistory.length - 1];
      setStepHistory(prevHistory);
      if (prevStep) {
        const q = (flow?.questions || []).find(q => q.id === prevStep.question_id);
        if (q) {
          const newData = { ...collectedData };
          delete newData[q.field_key];
          setCollectedData(newData);
        }
      }
      setCurrentQuestionId(r.data.question_id);
      setTextInput("");
      setMultiSelect([]);
    } catch (e) {
      // Fallback: local back
      if (stepHistory.length > 0) {
        const prev = stepHistory[stepHistory.length - 1];
        setStepHistory(prev => prev.slice(0, -1));
        setCurrentQuestionId(prev.question_id);
      }
    }
  }, [sessionId, stepHistory, flow, collectedData]);

  const handleSubmit = useCallback(async () => {
    if (!sessionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${BASE}/public/concierge/session/${sessionId}/submit`, {
        session_id: sessionId,
        journey_session_id: `concierge-${sessionId}`,
      });
      setPublicReference(r.data.public_reference);
      setSubmitted(true);
      clearSession();
    } catch (e) {
      const msg = e.response?.data?.detail || "Submission failed. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [sessionId]);

  const handleConsentAndSubmit = useCallback((consentGiven) => {
    if (!consentGiven) return;
    submitStep(currentQuestionId, true).then(() => {
      // After consent is recorded as a step, submit
      handleSubmit();
    });
  }, [currentQuestionId, submitStep, handleSubmit]);

  const handleMultiSelectSubmit = useCallback(() => {
    if (multiSelect.length > 0 || currentQuestion?.required === false) {
      submitStep(currentQuestionId, multiSelect.length > 0 ? multiSelect : []);
    }
  }, [multiSelect, currentQuestion, currentQuestionId, submitStep]);

  const handleWhatsAppClick = useCallback(() => {
    if (!sessionId) return;
    axios.post(`${BASE}/public/concierge/session/${sessionId}/whatsapp-click`, { mode: "MODE_A" }).catch(() => {});
  }, [sessionId]);

  const branding = flow?.branding || {};
  const accent = branding.accent_color || "#C9A15F";
  const displayName = branding.display_name || "Prashasta Concierge";
  const position = settings?.launcher_position || "bottom-right";

  // WhatsApp handoff config
  const waConfig = flow?.whatsapp_handoff_config || {};
  const waNumber = waConfig.business_number || "";
  const waPrefill = waConfig.prefill_template
    ? waConfig.prefill_template.replace("{public_reference}", publicReference || "")
    : `Hello Prashasta! My enquiry reference is ${publicReference || ""}`;
  const waUrl = buildWhatsAppUrl(waNumber, waPrefill);

  if (!settings?.enabled) return null;

  const posStyle = position === "bottom-left"
    ? { bottom: "24px", left: "24px" }
    : { bottom: "24px", right: "24px" };

  return (
    <>
      {/* Teaser badge */}
      {teaserVisible && !open && (
        <div
          data-testid="concierge-teaser"
          style={{
            position: "fixed",
            ...posStyle,
            ...(position === "bottom-left" ? { left: "80px" } : { right: "80px" }),
            zIndex: 9998,
            background: "#fff",
            border: "1.5px solid #e8dfc9",
            borderRadius: "20px",
            padding: "10px 16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "14px",
            color: "#3c3028",
            maxWidth: "240px",
            cursor: "pointer",
            animation: "concierge-fade-in 0.4s ease",
          }}
          onClick={handleOpen}
        >
          <span>{settings?.teaser_text || "Need help planning your event?"}</span>
          <button
            onClick={e => { e.stopPropagation(); setTeaserVisible(false); _safeSessSet(_popupSessKey(alias), "dismissed"); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0", color: "#9a8060", lineHeight: 1 }}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="concierge-panel"
          style={{
            position: "fixed",
            ...posStyle,
            zIndex: 9999,
            width: "min(380px, calc(100vw - 32px))",
            height: "min(620px, calc(100vh - 48px))",
            background: "#fff",
            borderRadius: "20px",
            boxShadow: "0 8px 48px rgba(50,35,15,0.18)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: "'DM Sans', sans-serif",
            animation: "concierge-slide-up 0.3s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          {/* Header */}
          <div style={{
            background: accent,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}>
            <div style={{
              width: "40px", height: "40px", borderRadius: "50%",
              background: "rgba(255,255,255,0.25)", display: "flex",
              alignItems: "center", justifyContent: "center",
              fontWeight: "700", color: "#fff", fontSize: "16px", flexShrink: 0,
            }}>
              {(branding.avatar_initials || "P")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontWeight: "600", fontSize: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {displayName}
              </div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px" }}>Online now</div>
            </div>
            <button
              data-testid="concierge-restart"
              onClick={handleRestart}
              title="Restart conversation"
              style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", padding: "4px" }}
            >
              <RotateCcw size={16} />
            </button>
            <button
              data-testid="concierge-close"
              onClick={handleClose}
              aria-label="Close"
              style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "4px" }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress bar */}
          {!submitted && (
            <div style={{ height: "3px", background: "#f0e8d8", flexShrink: 0 }}>
              <div style={{ height: "100%", width: `${progressPct}%`, background: accent, transition: "width 0.4s ease" }} />
            </div>
          )}

          {/* Chat body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Loading */}
            {loading && (
              <div style={{ textAlign: "center", color: "#9a8060", padding: "20px 0" }}>
                <div style={{ fontSize: "24px", animation: "concierge-spin 0.8s linear infinite", display: "inline-block" }}>⊙</div>
                <div style={{ fontSize: "13px", marginTop: "8px" }}>Starting your session…</div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div data-testid="concierge-error" style={{
                background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px",
                padding: "12px 16px", fontSize: "13px", color: "#b91c1c",
              }}>
                {error}
                <button onClick={() => setError(null)} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", marginLeft: "8px", fontWeight: "600" }}>✕</button>
              </div>
            )}

            {/* Welcome message */}
            {!loading && !error && stepHistory.length === 0 && flow?.welcome_message && !submitted && (
              <div style={{
                background: "#fdf8f0", borderRadius: "0 16px 16px 16px",
                padding: "14px 16px", fontSize: "14px", color: "#3c3028", lineHeight: "1.5",
                borderLeft: `3px solid ${accent}`,
              }}>
                {flow.welcome_message}
              </div>
            )}

            {/* Conversation history */}
            {stepHistory.map((step, i) => {
              const q = (flow?.questions || []).find(q => q.id === step.question_id);
              const displayAnswer = Array.isArray(step.answer)
                ? step.answer.join(", ")
                : step.answer === true ? "Agreed" : String(step.answer || "");
              return (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {/* Assistant message */}
                  <div style={{ background: "#fdf8f0", borderRadius: "0 12px 12px 12px", padding: "10px 14px", fontSize: "13px", color: "#5a4a3a", maxWidth: "85%" }}>
                    {step.question_text || q?.text}
                  </div>
                  {/* User answer */}
                  <div style={{ alignSelf: "flex-end", background: accent, color: "#fff", borderRadius: "12px 0 12px 12px", padding: "8px 14px", fontSize: "13px", maxWidth: "85%" }}>
                    {displayAnswer}
                  </div>
                </div>
              );
            })}

            {/* Current question */}
            {!loading && !submitted && currentQuestion && (
              <div data-testid="concierge-current-question" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {/* Question bubble */}
                <div style={{
                  background: "#fdf8f0",
                  borderRadius: "0 16px 16px 16px",
                  padding: "14px 16px",
                  fontSize: "14px",
                  color: "#3c3028",
                  lineHeight: "1.5",
                  borderLeft: `3px solid ${accent}`,
                  animation: "concierge-fade-in 0.3s ease",
                }}>
                  {currentQuestion.text}
                  {currentQuestion.required === false && (
                    <span style={{ fontSize: "11px", color: "#9a8060", marginLeft: "8px" }}>(optional)</span>
                  )}
                </div>

                {/* Single choice */}
                {currentQuestion.type === "SINGLE_CHOICE" && (
                  <>
                    <QuickReplies
                      options={currentQuestion.options || []}
                      selected={collectedData[currentQuestion.field_key]}
                      onSelect={val => submitStep(currentQuestion.id, val)}
                      multi={false}
                    />
                    {currentQuestion.required === false && (
                      <button
                        onClick={() => submitStep(currentQuestion.id, null)}
                        style={{ alignSelf: "flex-start", background: "none", border: "none", color: "#9a8060", fontSize: "13px", cursor: "pointer", textDecoration: "underline", marginTop: "4px" }}
                      >
                        {currentQuestion.is_optional_label || "Skip"}
                      </button>
                    )}
                  </>
                )}

                {/* Multi choice */}
                {currentQuestion.type === "MULTI_CHOICE" && (
                  <>
                    <QuickReplies
                      options={currentQuestion.options || []}
                      selected={multiSelect}
                      onSelect={val => setMultiSelect(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])}
                      multi={true}
                    />
                    <button
                      data-testid="concierge-multi-next"
                      onClick={handleMultiSelectSubmit}
                      style={{
                        alignSelf: "flex-start", marginTop: "8px",
                        padding: "9px 20px", borderRadius: "20px",
                        background: accent, border: "none", color: "#fff",
                        fontSize: "14px", cursor: "pointer", fontWeight: "600",
                      }}
                    >
                      {multiSelect.length > 0 ? `Next →` : "Skip →"}
                    </button>
                  </>
                )}

                {/* Text / Phone / Email */}
                {["TEXT", "PHONE", "EMAIL"].includes(currentQuestion.type) && (
                  <>
                    <TextInput
                      placeholder={currentQuestion.placeholder}
                      value={textInput}
                      onChange={setTextInput}
                      onSubmit={() => { if (textInput.trim()) submitStep(currentQuestion.id, textInput.trim()); }}
                      type={currentQuestion.type === "EMAIL" ? "email" : currentQuestion.type === "PHONE" ? "tel" : "text"}
                    />
                    {currentQuestion.required === false && (
                      <button
                        onClick={() => submitStep(currentQuestion.id, null)}
                        style={{ alignSelf: "flex-start", background: "none", border: "none", color: "#9a8060", fontSize: "13px", cursor: "pointer", textDecoration: "underline", marginTop: "4px" }}
                      >
                        {currentQuestion.is_optional_label || "Skip"}
                      </button>
                    )}
                  </>
                )}

                {/* Date */}
                {currentQuestion.type === "DATE" && (
                  <TextInput
                    placeholder={currentQuestion.placeholder || "YYYY-MM-DD"}
                    value={textInput}
                    onChange={setTextInput}
                    onSubmit={() => { if (textInput.trim()) submitStep(currentQuestion.id, textInput.trim()); }}
                    type="date"
                  />
                )}

                {/* Consent */}
                {currentQuestion.type === "CONSENT" && (
                  <div style={{ background: "#fdf8f0", borderRadius: "12px", padding: "14px 16px", fontSize: "13px", color: "#5a4a3a" }}>
                    <p style={{ margin: "0 0 12px", lineHeight: "1.5" }}>
                      {flow?.consent_text || "I consent to Prashasta Events contacting me about my event enquiry."}
                    </p>
                    <button
                      data-testid="concierge-consent-agree"
                      onClick={() => handleConsentAndSubmit(true)}
                      disabled={submitting}
                      style={{
                        width: "100%", padding: "12px", borderRadius: "12px",
                        background: submitting ? "#e0d5c0" : accent,
                        border: "none", color: "#fff", fontSize: "14px",
                        cursor: submitting ? "not-allowed" : "pointer", fontWeight: "600",
                      }}
                    >
                      {submitting ? "Submitting…" : "I Agree — Submit My Enquiry"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* All questions answered but consent not yet shown */}
            {!loading && !submitted && !currentQuestion && !currentQuestionId && stepHistory.length > 0 && (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: "13px", color: "#9a8060", marginBottom: "12px" }}>Ready to submit your enquiry?</div>
                <button
                  data-testid="concierge-submit-final"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    padding: "12px 28px", borderRadius: "12px",
                    background: submitting ? "#e0d5c0" : accent,
                    border: "none", color: "#fff", fontSize: "14px",
                    cursor: submitting ? "not-allowed" : "pointer", fontWeight: "600",
                  }}
                >
                  {submitting ? "Submitting…" : "Submit Enquiry →"}
                </button>
              </div>
            )}

            {/* Submitted */}
            {submitted && (
              <EnquiryConfirmationScreen
                publicReference={publicReference}
                whatsappUrl={waConfig.enabled !== false ? waUrl : null}
                onWhatsAppClick={handleWhatsAppClick}
                onRestart={handleRestart}
                testIdPrefix="concierge"
              />
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Back navigation */}
          {!submitted && stepHistory.length > 0 && currentQuestion && (
            <div style={{
              padding: "12px 20px", borderTop: "1px solid #f0e8d8",
              display: "flex", alignItems: "center", gap: "8px", flexShrink: 0,
            }}>
              <button
                data-testid="concierge-back"
                onClick={handleBack}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  background: "none", border: "none", color: "#9a8060",
                  fontSize: "13px", cursor: "pointer", padding: "4px 8px",
                  borderRadius: "8px",
                }}
              >
                <ChevronLeft size={16} />
                Back
              </button>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: "11px", color: "#b8a88a" }}>
                {answeredQs} of {totalQs - 1} steps
              </span>
            </div>
          )}
        </div>
      )}

      {/* Launcher button */}
      <button
        data-testid="concierge-launcher"
        onClick={open ? handleClose : handleOpen}
        aria-label={open ? "Close chat" : "Open chat assistant"}
        style={{
          position: "fixed",
          ...posStyle,
          zIndex: 9999,
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          background: open ? "#6b7280" : accent,
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          transition: "all 0.2s ease",
          color: "#fff",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>

      {/* Inline CSS animations */}
      <style>{`
        @keyframes concierge-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes concierge-slide-up { from { opacity: 0; transform: translateY(24px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes concierge-spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
